import {
  type AudioRecordingController,
  checkAudioRecordingSupport,
  startAudioRecording,
} from './audio-recording-client.ts';
import { canEncodeDictationAudio, encodeDictationAudio } from './dictation-audio.ts';
import { dictationCopy } from './dictation-copy.ts';
import type { SupportedLocale } from './i18n/index.ts';
export type TranscribeDraft = (
  blob: Blob,
  attemptId: string,
  signal: AbortSignal,
) => Promise<string>;
export interface DictationUi {
  busy(): boolean;
  cancel(): void;
  dispose(): void;
}
type Field = HTMLInputElement | HTMLTextAreaElement;
interface Control {
  field: Field;
  button: HTMLButtonElement;
  path: SVGPathElement;
  wave: SVGSVGElement;
  status: HTMLSpanElement;
  label: string;
}
const MIC = 'M9 5a3 3 0 0 1 6 0v7a3 3 0 0 1-6 0V5M5 10v2a7 7 0 0 0 14 0v-2M12 19v3M8 22h8';
const STOP = 'M6 6h12v12H6z';
const SPINNER = 'M12 3a9 9 0 1 1-9 9';
/** Draft audio stays transient; only the existing authenticated PCM transcription endpoint receives it. */
export function installDictation(
  panel: HTMLElement,
  locale: SupportedLocale,
  transcribe: TranscribeDraft,
): DictationUi {
  const form = panel.querySelector('form');
  if (!form || !checkAudioRecordingSupport().supported || !canEncodeDictationAudio())
    return {
      busy: () => false,
      cancel() {
        /* unsupported */
      },
      dispose() {
        /* unsupported */
      },
    };
  const m = dictationCopy(locale),
    controls: Control[] = [];
  const submit = form.querySelector<HTMLButtonElement>('.submit');
  let phase = 'idle',
    epoch = 0,
    controller: AudioRecordingController | null = null,
    request: AbortController | null = null,
    target: Control | null = null;
  let lockedSubmit = false,
    submitWasDisabled = false,
    disposed = false,
    cleanupWave = () => {
      /* no active resource */
    };
  const busy = () => ['permission', 'recording', 'processing'].includes(phase);
  function setPhase(next: string, message = '') {
    phase = next;
    const active = busy();
    if (submit && active && !lockedSubmit) {
      submitWasDisabled = submit.disabled;
      lockedSubmit = true;
      submit.disabled = true;
    }
    if (submit && !active && lockedSubmit) {
      submit.disabled = submitWasDisabled;
      lockedSubmit = false;
    }
    for (const c of controls) {
      const selected = c === target;
      const recording = selected && next === 'recording';
      c.button.disabled = active ? !recording : Boolean(submit?.disabled);
      c.button.dataset.dictationState = selected ? next : 'idle';
      c.button.setAttribute('aria-pressed', String(recording));
      c.button.setAttribute(
        'aria-label',
        `${recording ? m.stop : selected && next === 'processing' ? m.processing : m.speak}: ${c.label}`,
      );
      c.path.setAttribute(
        'd',
        recording ? STOP : selected && next === 'processing' ? SPINNER : MIC,
      );
      c.path.setAttribute('fill', recording ? 'currentColor' : 'none');
      c.wave.toggleAttribute('hidden', !recording);
      c.field.closest('.dictation-input')?.classList.toggle('dictation-active', recording);
      c.status.textContent = selected
        ? next === 'permission'
          ? m.start
          : next === 'processing'
            ? m.processing
            : message
        : '';
      c.status.classList.toggle(
        'dictation-status-quiet',
        next === 'permission' || next === 'processing',
      );
      c.status.hidden = !c.status.textContent;
    }
  }
  function clearCapture() {
    cleanupWave();
    cleanupWave = () => {
      /* no active resource */
    };
    request?.abort();
    request = null;
    controller?.cancel();
    controller = null;
  }
  function cancel() {
    epoch++;
    clearCapture();
    setPhase('idle');
  }
  function fail(message = m.error) {
    epoch++;
    clearCapture();
    setPhase('error', message);
  }
  function visualise(stream: MediaStream, id: number, c: Control) {
    if (id !== epoch || disposed || panel.hidden) return;
    let context: AudioContext | undefined,
      source: MediaStreamAudioSourceNode | undefined,
      analyser: AnalyserNode | undefined,
      frame: number | undefined,
      closed = false;
    const cleanup = () => {
      if (closed) return;
      closed = true;
      if (frame !== undefined) cancelAnimationFrame(frame);
      try {
        source?.disconnect();
        analyser?.disconnect();
      } catch {
        /* optional analyser already released or unavailable */
      }
      try {
        void context?.close().catch(() => {
          /* no active resource */
        });
      } catch {
        /* optional analyser already released or unavailable */
      }
    };
    try {
      // The analyser consumes the already-authorized recording stream, never a second microphone or speakers.
      context = new AudioContext();
      source = context.createMediaStreamSource(stream);
      analyser = context.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      const samples = new Uint8Array(analyser.fftSize);
      const bars = [...c.wave.querySelectorAll('rect')];
      const meter = analyser;
      const draw = () => {
        if (closed || id !== epoch) return;
        meter.getByteTimeDomainData(samples);
        for (let index = 0; index < bars.length; index++) {
          let sum = 0;
          const start = Math.floor((index * samples.length) / bars.length),
            end = Math.floor(((index + 1) * samples.length) / bars.length);
          for (let n = start; n < end; n++) sum += (((samples[n] ?? 128) - 128) / 128) ** 2;
          const height = Math.max(2, Math.min(16, Math.sqrt(sum / Math.max(1, end - start)) * 32));
          bars[index]?.setAttribute('height', height.toFixed(2));
          bars[index]?.setAttribute('y', ((18 - height) / 2).toFixed(2));
        }
        frame = requestAnimationFrame(draw);
      };
      cleanupWave = cleanup;
      void context.resume().catch(() => {
        /* no active resource */
      });
      draw();
    } catch {
      cleanup();
    }
  }
  async function finish(id: number) {
    if (id !== epoch || phase !== 'recording' || !controller || !target) return;
    const selected = target,
      active = controller;
    setPhase('processing');
    cleanupWave();
    cleanupWave = () => {
      /* no active resource */
    };
    try {
      const result = await active.stop();
      if (id !== epoch || disposed) return;
      controller = null;
      if (result.blob.size === 0 || result.blob.size > 4 * 1024 * 1024)
        throw Error('capture_invalid');
      request = new AbortController();
      const signal = request.signal;
      const audio = await encodeDictationAudio(result.blob, signal);
      if (id !== epoch || signal.aborted) return;
      const text = (await transcribe(audio, crypto.randomUUID(), signal)).trim();
      if (id !== epoch || signal.aborted || disposed) return;
      request = null;
      if (!selected.field.isConnected || panel.hidden) {
        cancel();
        return;
      }
      if (!text) {
        setPhase('error', m.empty);
        return;
      }
      const before = selected.field.value,
        separator = before.trim() ? (selected.field.tagName === 'INPUT' ? ' ' : '\n') : '';
      const after = before + separator + text;
      if (selected.field.maxLength >= 0 && after.length > selected.field.maxLength) {
        setPhase('error', m.overflow);
        return;
      }
      selected.field.value = after;
      selected.field.dispatchEvent(new Event('input', { bubbles: true }));
      setPhase('idle');
    } catch {
      if (id === epoch && !disposed) fail();
    }
  }
  for (const field of form.querySelectorAll<Field>(
    'input[name="title"],textarea[name="summary"]',
  )) {
    const wrapper = field.closest('.field');
    if (!wrapper) continue;
    const label = document.createElement('label');
    field.id = `oncatch-dictation-${field.name}`;
    label.htmlFor = field.id;
    const labelText = wrapper.querySelector('.field-label-text');
    if (labelText) label.append(labelText);
    const container = document.createElement('div');
    container.className = wrapper.className;
    wrapper.replaceWith(container);
    const input = document.createElement('div');
    input.className = `dictation-input${field.tagName === 'TEXTAREA' ? ' dictation-input-multiline' : ''}`;
    container.append(label, input);
    input.append(field);
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dictation-speak';
    const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '2');
    icon.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS(icon.namespaceURI, 'path') as SVGPathElement;
    icon.append(path);
    button.append(icon);
    const wave = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    wave.classList.add('dictation-waveform');
    wave.setAttribute('viewBox', '0 0 36 18');
    wave.setAttribute('aria-hidden', 'true');
    wave.setAttribute('hidden', '');
    for (let n = 0; n < 12; n++) {
      const bar = document.createElementNS(icon.namespaceURI, 'rect');
      bar.setAttribute('x', String(n * 3));
      bar.setAttribute('width', '2');
      bar.setAttribute('y', '8');
      bar.setAttribute('height', '2');
      wave.append(bar);
    }
    const status = document.createElement('span');
    status.className = 'dictation-status';
    status.id = `oncatch-dictation-status-${field.name}`;
    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');
    status.setAttribute('aria-atomic', 'true');
    status.hidden = true;
    const labelName = (label.textContent ?? field.name).replace(/\s*\*$/, '').trim();
    button.title = `${m.speak}: ${labelName}. ${m.consent}`;
    button.setAttribute('aria-description', m.consent);
    button.setAttribute('aria-controls', field.id);
    button.setAttribute('aria-describedby', status.id);
    input.append(wave, button);
    container.append(status);
    const control = { field, button, path, wave, status, label: labelName };
    controls.push(control);
    button.addEventListener('click', () => {
      if (disposed) return;
      if (phase === 'recording' && target === control) {
        void finish(epoch);
        return;
      }
      if (busy() || submit?.disabled) return;
      cancel();
      target = control;
      const id = ++epoch;
      setPhase('permission');
      // Preserve existing 59s headroom and strict PCM/source bounds unchanged.
      void startAudioRecording({
        maxDurationMs: 59000,
        maxBytes: 4 * 1024 * 1024,
        onStream: (stream) => visualise(stream, id, control),
      })
        .then((active) => {
          if (id !== epoch || disposed) {
            active.cancel();
            return;
          }
          if (panel.hidden) {
            active.cancel();
            cancel();
            return;
          }
          controller = active;
          setPhase('recording');
          active.onStateChange((next) => {
            if (id !== epoch) return;
            if (next === 'stopped' && phase === 'recording') void finish(id);
            else if (next === 'error') fail();
          });
        })
        .catch(() => {
          if (id === epoch && !disposed) fail();
        });
    });
  }
  setPhase('idle');
  const root = panel.getRootNode();
  const keydown = (event: Event) => {
    if ((event as KeyboardEvent).key === 'Escape' && busy()) {
      cancel();
    }
  };
  root.addEventListener('oncatch:panel-close', cancel);
  panel.addEventListener('keydown', keydown);
  window.addEventListener('pagehide', cancel);
  const observer = new MutationObserver(() => {
    if (panel.hidden) cancel();
  });
  observer.observe(panel, { attributes: true, attributeFilter: ['hidden'] });
  return {
    busy,
    cancel,
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      observer.disconnect();
      root.removeEventListener('oncatch:panel-close', cancel);
      panel.removeEventListener('keydown', keydown);
      window.removeEventListener('pagehide', cancel);
      for (const c of controls) {
        c.button.remove();
        c.wave.remove();
        c.status.remove();
      }
    },
  };
}
